import React from "react"
import { Box, Card, CardContent, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { Delete, Edit, FindReplace } from "@mui/icons-material"
import { GallerySection } from "@/shared/types/product"
import { Prompt } from "@/shared/types/prompt"
import { EditableGallerySectionField } from "@/features/dashboard/hooks/useGallery"
import ProductForm from "@/features/dashboard/components/ProductForm"
import PromptsPanel from "@/features/dashboard/components/PromptsPanel"

interface Props {
  section: GallerySection
  sectionIndex: number
  maxSelected: number
  prompts: Prompt[]
  promptsLoading: boolean
  promptsError: string | null
  customPromptSaving: boolean
  customPromptError: string | null
  onFieldChange: (sectionId: number, field: EditableGallerySectionField, value: string) => void
  onKeywordsChange: (sectionId: number, keywords: string[]) => void
  onPromptToggle: (sectionId: number, promptId: string) => void
  onSaveCustomPrompt: (promptText: string) => Promise<string>
  onDeleteCustomPrompt: (promptId: string) => Promise<void>
  onView: (images: string[], initialIndex: number) => void
  onOpenEditor: (sectionId: number, imageIndex: number) => void
  onReplaceImage: (sectionId: number, imageIndex: number) => void
  onRemoveSection: (sectionId: number) => void
  generatingText: boolean
  textGenerationBusy: boolean
  onGenerateText: (sectionId: number) => void | Promise<void>
}

const OVERLAY_BG = "rgba(0,0,0,0.45)"

const ProductCard: React.FC<Props> = ({
  section,
  sectionIndex,
  maxSelected,
  prompts,
  promptsLoading,
  promptsError,
  customPromptSaving,
  customPromptError,
  onView,
  onFieldChange,
  onKeywordsChange,
  onPromptToggle,
  onSaveCustomPrompt,
  onDeleteCustomPrompt,
  onOpenEditor,
  onReplaceImage,
  onRemoveSection,
  generatingText,
  textGenerationBusy,
  onGenerateText
}) => {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
          {/* Left panel — image + thumbnails */}
          <Box sx={{ flex: "0 0 auto", width: { xs: "100%", md: "52%" } }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {section.name || `Product ${sectionIndex + 1}`}
            </Typography>

            {/* Main image with zoom */}
            <Box
              sx={{ position: "relative", overflow: "hidden", borderRadius: 1 }}
              tabIndex={0}
              aria-label="Product image preview">
              <Box
                component="img"
                onClick={() => {
                  onView(section.images, section.selectedIndex)
                }}
                src={section.images[section.selectedIndex]}
                alt={`Preview of gallery ${sectionIndex + 1}`}
                sx={{
                  width: "100%",
                  height: { xs: 220, sm: 340, md: 380 },
                  objectFit: "contain",
                  bgcolor: "grey.100",
                  display: "block",
                  transition: "transform 0.2s ease",
                  "&:hover": { transform: "scale(1.03)" }
                }}
              />

              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  position: "absolute",
                  bottom: 8,
                  right: 8
                }}>
                <Tooltip title="Replace image">
                  <IconButton
                    size="small"
                    onClick={() => onReplaceImage(section.id, section.selectedIndex)}
                    sx={{
                      bottom: 8,
                      right: 8,
                      bgcolor: OVERLAY_BG,
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.65)" }
                    }}>
                    <FindReplace fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove section">
                  <IconButton
                    size="small"
                    onClick={() => onRemoveSection(section.id)}
                    sx={{
                      bottom: 8,
                      right: 8,
                      bgcolor: OVERLAY_BG,
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.65)" }
                    }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit image">
                  <IconButton
                    size="small"
                    onClick={() => onOpenEditor(section.id, section.selectedIndex)}
                    sx={{
                      bottom: 8,
                      right: 8,
                      bgcolor: OVERLAY_BG,
                      color: "#fff",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.65)" }
                    }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            <ProductForm
              section={section}
              onFieldChange={onFieldChange}
              onKeywordsChange={onKeywordsChange}
              generatingText={generatingText}
              textGenerationBusy={textGenerationBusy}
              canGenerateText={Boolean(section.files[section.selectedIndex])}
              onGenerateText={() => onGenerateText(section.id)}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflowY: { md: "auto" },
              display: "flex",
              flexDirection: "column"
            }}>
            <PromptsPanel
              section={section}
              maxSelected={maxSelected}
              prompts={prompts}
              loading={promptsLoading}
              error={promptsError}
              onPromptToggle={onPromptToggle}
              onSaveCustomPrompt={onSaveCustomPrompt}
              onDeleteCustomPrompt={onDeleteCustomPrompt}
              customPromptSaving={customPromptSaving}
              customPromptError={customPromptError}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default ProductCard
